import { Component, DestroyRef, inject, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { PaypalService, PaypalLogDTO } from '../../../core/services/register/servicesPaypal/paypal/paypal.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * PAYPAL ESSENTIEL COMPONENT - Frontend component for Essentiel subscription
 */
@Component({
    selector: 'app-paypal-essentiel',
    templateUrl: './paypal-essentiel.component.html',
    styleUrls: ['./paypal-essentiel.component.scss'],
    imports: [CommonModule, FormsModule, TranslateModule]
})
export class PaypalEssentielComponent implements OnInit, OnChanges {
  @Input() idUser!: number;
  @Input() paymentMode?: 'ESSENTIEL_MENSUEL' | 'ESSENTIEL_ANNUEL';

  internalPaymentMode: 'ESSENTIEL_MENSUEL' | 'ESSENTIEL_ANNUEL' = 'ESSENTIEL_ANNUEL';

  loaded = false;
  paypalButtonId = '';
  paypalActionUrl = '';

  private destroyRef = inject(DestroyRef);
  private paypalService = inject(PaypalService);

  private essentielButtonId = '';
  private essentielNoTrialButtonId = '';

  ngOnInit() {
    this.paypalService.buttonProperties()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (props) => {
          this.essentielButtonId = props.essentielButtonId ?? '';
          this.essentielNoTrialButtonId = props.essentielNoTrialButtonId ?? '';
          this.paypalActionUrl = props.paypalActionUrl ?? '';
          this.updateButtonId();
          this.loaded = true;
        },
        error: (err) => {
          console.error('[PayPal][Essentiel] Error fetching button properties:', err);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update button ID when payment mode changes
    if (changes['paymentMode'] && this.loaded) {
      this.updateButtonId();
    }
    // Sync internalPaymentMode with paymentMode input if provided
    if (changes['paymentMode'] && this.paymentMode) {
      this.internalPaymentMode = this.paymentMode;
    }
  }

  private updateButtonId() {
    this.paypalButtonId = this.getButtonId();
  }

  get effectivePaymentMode() {
    return this.paymentMode ?? this.internalPaymentMode;
  }

  private getButtonId(): string {
    // Use no-trial button for annual, regular button for monthly
    // Adjust this logic based on your PayPal button configuration
    if (this.effectivePaymentMode === 'ESSENTIEL_ANNUEL' && this.essentielNoTrialButtonId) {
      return this.essentielNoTrialButtonId;
    }
    return this.essentielButtonId;
  }

  /**
   * Get the os0 value for PayPal form submission
   * PayPal buttons now use "Mensuel" or "Annuel" for all environments
   */
  private getOs0Value(): string {
    return this.effectivePaymentMode === 'ESSENTIEL_ANNUEL' ? 'Annuel' : 'Mensuel';
  }

  subscribe() {
    if (!this.idUser || this.idUser == 0) {
      alert('Erreur: ID utilisateur manquant. Veuillez réessayer.');
      return;
    }
    
    const buttonId = this.getButtonId();
    
    if (!buttonId || buttonId.trim() === '') {
      alert('Erreur: Le bouton PayPal n\'est pas configuré. Veuillez contacter le support.');
      return;
    }

    if (!this.paypalActionUrl || this.paypalActionUrl.trim() === '') {
      alert('Erreur: L\'URL PayPal n\'est pas configurée. Veuillez contacter le support.');
      return;
    }
    
    const paymentMode = this.effectivePaymentMode === 'ESSENTIEL_ANNUEL' ? 'ANNUEL' : 'MENSUEL';
    
    const log: PaypalLogDTO = {
      actionType: 'SUBSCRIPTION',
      idUser: this.idUser,
      paymentMode: paymentMode,
      paypalButtonId: buttonId,
      subscriptionType: 'ESSENTIEL'
    };
    
    this.paypalService.logPublic(log)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const form = document.createElement('form');
          form.method = 'post';
          form.target = '_top';
          form.action = this.paypalActionUrl;

          const os0Value = this.getOs0Value();
          const returnUrl = `${window.location.origin}/authentication/login`;
          
          form.innerHTML = `
            <input type="hidden" name="cmd" value="_s-xclick">
            <input type="hidden" name="hosted_button_id" value="${buttonId}">
            <input type="hidden" name="on0" value="Souscription-Budgetfacile-Essentiel">
            <input type="hidden" name="os0" value="${os0Value}">
            <input type="hidden" name="currency_code" value="EUR">
            <input type="hidden" name="custom" value="${this.idUser}">
            <input type="hidden" name="return" value="${returnUrl}">
            <input type="hidden" name="rm" value="2">
            <input type="hidden" name="cancel_return" value="${returnUrl}">
          `;
          
          document.body.appendChild(form);
            form.submit();
        },
        error: (err) => {
          console.error('[PayPal][Essentiel] Error logging subscription:', err);
          const form = document.createElement('form');
          form.method = 'post';
          form.target = '_top';
          form.action = this.paypalActionUrl;

          const os0Value = this.getOs0Value();
          const returnUrl = `${window.location.origin}/authentication/login`;

          form.innerHTML = `
            <input type="hidden" name="cmd" value="_s-xclick">
            <input type="hidden" name="hosted_button_id" value="${buttonId}">
            <input type="hidden" name="on0" value="Souscription-Budgetfacile-Essentiel">
            <input type="hidden" name="os0" value="${os0Value}">
            <input type="hidden" name="currency_code" value="EUR">
            <input type="hidden" name="custom" value="${this.idUser}">
            <input type="hidden" name="return" value="${returnUrl}">
            <input type="hidden" name="rm" value="2">
            <input type="hidden" name="cancel_return" value="${returnUrl}">
          `;
          document.body.appendChild(form);
          form.submit();
        }
      });
  }
}
